import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Settings2, X, Check } from 'lucide-react';
import {
  getConsent,
  setConsent,
  acceptAll,
  rejectAll,
  CONSENT_EVENT,
} from '../lib/consent';

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [preferences, setPreferences] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (!consent) {
      setVisible(true);
    } else {
      setAnalytics(!!consent.analytics);
      setPreferences(!!consent.preferences);
      // Restore Google Consent Mode from saved preference
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          analytics_storage: consent.analytics ? 'granted' : 'denied',
        });
      }
    }

    const openHandler = () => {
      const c = getConsent();
      if (c) {
        setAnalytics(!!c.analytics);
        setPreferences(!!c.preferences);
      }
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener('allorep:open-cookie-settings', openHandler);
    return () => window.removeEventListener('allorep:open-cookie-settings', openHandler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (!e.detail) {
        setVisible(true);
        setShowDetails(false);
      }
    };
    window.addEventListener(CONSENT_EVENT, handler);
    return () => window.removeEventListener(CONSENT_EVENT, handler);
  }, []);

  // Sync Google Consent Mode whenever consent changes
  const updateGtagConsent = (analyticsGranted) => {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: analyticsGranted ? 'granted' : 'denied',
      });
    }
  };

  const handleAcceptAll = () => {
    acceptAll();
    setAnalytics(true);
    setPreferences(true);
    updateGtagConsent(true);
    setVisible(false);
    setShowDetails(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setAnalytics(false);
    setPreferences(false);
    updateGtagConsent(false);
    setVisible(false);
    setShowDetails(false);
  };

  const handleSavePreferences = () => {
    setConsent({ analytics, preferences });
    updateGtagConsent(analytics);
    setVisible(false);
    setShowDetails(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-sm"
        aria-hidden="true"
        onClick={() => {
          if (showDetails) setShowDetails(false);
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-description"
        className="fixed inset-x-3 bottom-3 sm:inset-x-4 sm:bottom-4 lg:right-4 lg:left-auto lg:bottom-4 lg:max-w-xl z-[70] bg-white rounded-3xl shadow-2xl shadow-slate-900/20 border border-slate-100 overflow-hidden"
        data-testid="cookie-banner"
      >
        <div className="h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600" />

        <div className="p-6 lg:p-7">
          {!showDetails ? (
            <>
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                  <Cookie className="text-blue-600" size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2
                    id="cookie-banner-title"
                    className="text-lg font-bold text-slate-900 mb-1"
                  >
                    Vos cookies, votre choix
                  </h2>
                  <p
                    id="cookie-banner-description"
                    className="text-sm text-slate-600 leading-relaxed"
                  >
                    Nous utilisons des cookies pour faire fonctionner le site et,
                    avec votre accord, mesurer son audience. Vous pouvez accepter,
                    refuser ou personnaliser à tout moment. Détails dans notre{' '}
                    <Link
                      to="/politique-confidentialite"
                      className="text-blue-600 hover:text-blue-700 underline underline-offset-2"
                    >
                      politique de confidentialité
                    </Link>
                    .
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={handleRejectAll}
                  className="px-4 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium text-sm transition-colors"
                  data-testid="cookie-reject-all"
                >
                  Tout refuser
                </button>
                <button
                  type="button"
                  onClick={() => setShowDetails(true)}
                  className="px-4 py-3 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium text-sm transition-colors flex items-center justify-center gap-2"
                  data-testid="cookie-customize"
                >
                  <Settings2 size={16} />
                  Personnaliser
                </button>
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  className="px-4 py-3 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 hover:shadow-lg hover:shadow-blue-500/30 transition-all"
                  data-testid="cookie-accept-all"
                >
                  Tout accepter
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-start gap-4 mb-5">
                <div className="flex-shrink-0 w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                  <Settings2 className="text-blue-600" size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-bold text-slate-900 mb-1">
                    Personnaliser mes choix
                  </h2>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Chaque catégorie peut être activée ou désactivée
                    indépendamment. Votre choix est conservé 13 mois.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDetails(false)}
                  className="flex-shrink-0 w-9 h-9 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-900 flex items-center justify-center transition-colors"
                  aria-label="Fermer"
                  data-testid="cookie-close-details"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="space-y-3 mb-5 max-h-[55vh] overflow-y-auto pr-1">
                <CategoryRow
                  testId="cookie-cat-necessary"
                  title="Strictement nécessaires"
                  description="Indispensables au fonctionnement du site (sécurité, mémorisation de votre choix de cookies). Ces cookies ne peuvent pas être désactivés."
                  enabled
                  required
                  onToggle={() => {}}
                />
                <CategoryRow
                  testId="cookie-cat-analytics"
                  title="Mesure d’audience"
                  description="Nous aident à comprendre comment vous utilisez le site (pages consultées, durée de visite). Anonymisé et limité à 13 mois."
                  enabled={analytics}
                  onToggle={() => setAnalytics((v) => !v)}
                />
                <CategoryRow
                  testId="cookie-cat-preferences"
                  title="Personnalisation"
                  description="Mémorisent vos préférences d’affichage pour vous offrir une expérience adaptée lors de votre prochaine visite."
                  enabled={preferences}
                  onToggle={() => setPreferences((v) => !v)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={handleRejectAll}
                  className="px-4 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium text-sm transition-colors"
                  data-testid="cookie-detail-reject-all"
                >
                  Tout refuser
                </button>
                <button
                  type="button"
                  onClick={handleSavePreferences}
                  className="px-4 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-medium text-sm transition-colors flex items-center justify-center gap-2"
                  data-testid="cookie-save-prefs"
                >
                  <Check size={16} />
                  Enregistrer
                </button>
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  className="px-4 py-3 rounded-xl text-white font-semibold text-sm bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 hover:shadow-lg hover:shadow-blue-500/30 transition-all"
                  data-testid="cookie-detail-accept-all"
                >
                  Tout accepter
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

const CategoryRow = ({ testId, title, description, enabled, required, onToggle }) => (
  <div
    className="p-4 rounded-2xl border border-slate-100 bg-slate-50/60 hover:border-slate-200 transition-colors"
    data-testid={testId}
  >
    <div className="flex items-start justify-between gap-4 mb-1">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
        {required && (
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-200 px-2 py-0.5 rounded-full">
            Toujours actif
          </span>
        )}
      </div>
      <Toggle enabled={enabled} disabled={required} onToggle={onToggle} />
    </div>
    <p className="text-xs text-slate-600 leading-relaxed">{description}</p>
  </div>
);

const Toggle = ({ enabled, disabled, onToggle }) => (
  <button
    type="button"
    role="switch"
    aria-checked={enabled}
    disabled={disabled}
    onClick={onToggle}
    className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors ${
      enabled
        ? 'bg-gradient-to-r from-blue-500 to-indigo-600'
        : 'bg-slate-300'
    } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span
      className={`absolute top-0.5 h-5 w-5 bg-white rounded-full shadow-sm transition-all ${
        enabled ? 'left-[22px]' : 'left-0.5'
      }`}
    />
  </button>
);

export default CookieBanner;
